import React, { useState, useMemo, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";

import PageLayout from "../_layout/PageLayout";
import { CurrentJobStorageModel } from "../../services/job/CurrentJobStorage";
import {
  RemoveSewerObjectsFromJob,
  RemoveSewerObjectsFromAssignedJob,
  ViewSewerObjectsLinkedToJob,
  ViewSewerObjectsLinkedToAssignedJobOrJobProject,
} from "../../services/authorization/Permissions";
import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import { AssignSewerObjectsToJob } from "./components/buttons/AssignSewerObjectsToJob";
import RemoveButton from "../../components/buttons/remove-button/RemoveButton";
import useErrorHandler, {
  ApplicationError,
} from "../../services/error-handler/ErrorHandlerService";
import { DownloadSewerObjectsButtons } from "../../components/buttons/download-sewer-objects/DownloadSewerObjectsButtons";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { api } from "../../services/api/ApiService";
import { JobSewerObjectsStorageModel } from "../../services/sewer-object/JobSewerObjectsStorage";
import useUrlParams from "../../services/url/UrlParamsService";
import { JobMenuItemsModel } from "./JobMenuItems";
import DataGrid from "../../components/datatable/DataGrid";
import { currentUserService } from "../../services/user/CurrentUserStorage";
import { useHistory } from "react-router";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../services/breadcrumbs/EntityTypeFromPathModel";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();
  const urlParamsService = useUrlParams();
  const currentJobId = urlParamsService.getJobId()!;

  const [currentJobStorage] = useState(() =>
    CurrentJobStorageModel.create({ currentJobId: currentJobId }, { api })
  );
  const [jobsSewerObjectsStorage] = useState(() =>
    JobSewerObjectsStorageModel.create({ jobId: currentJobId, useGridFiltering: true }, { api })
  );

  const jobMenuItems = useMemo(() => {
    return JobMenuItemsModel.create({
      jobId: currentJobId,
      isLoading: currentJobStorage.isLoading,
      numberOfSewerObjects: currentJobStorage.currentJob.sewerObjectsCount,
    });
  }, [currentJobId, currentJobStorage.currentJob.sewerObjectsCount, currentJobStorage.isLoading]);

  const isRemoveEnabled = useCallback(
    () =>
      (permissionsService.userHasPermissionTo(RemoveSewerObjectsFromJob) ||
        (permissionsService.userHasPermissionTo(RemoveSewerObjectsFromAssignedJob) &&
          currentJobStorage.currentUserIsAssignedToJob)) &&
      jobsSewerObjectsStorage.anyItemsSelected,
    [currentJobStorage.currentUserIsAssignedToJob, jobsSewerObjectsStorage.anyItemsSelected]
  );

  const menuItems = {
    staticItems: jobMenuItems.getStaticItems(),
  };

  const removeSelectedSewerObjects = useCallback(async () => {
    try {
      await jobsSewerObjectsStorage.removeSewerObjectsFromJob(
        currentJobId,
        jobsSewerObjectsStorage.selectedItems
      );
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.job.remove-sections");
      handleErrorInSnackbar(applicationError);
    }
  }, [jobsSewerObjectsStorage, currentJobId, handleErrorInSnackbar]);

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([
      {
        entity: EntityTypeFromPath.Job,
        name: currentJobStorage.currentJob.name,
      },
    ]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentJobStorage.currentJob.name, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[
        ViewSewerObjectsLinkedToJob,
        ViewSewerObjectsLinkedToAssignedJobOrJobProject,
      ]}
      titleKey="JobSewerObjectsPage.title"
      titleContext={{
        jobName: currentJobStorage.currentJob && currentJobStorage.currentJob.name,
      }}
      menuItems={menuItems}
      id="job-sewer-objects"
    >
      <ActionsSection>
        <ButtonsSection>
          <AssignSewerObjectsToJob
            jobSewerObjectsStorage={jobsSewerObjectsStorage}
            isJobAssignedToProject={!!currentJobStorage.currentJob.project?.id}
            reloadSewerObjectsMap={async () => {}}
            currentUserAssignedToJob={
              currentJobStorage.currentJob.assignedUser?.id === currentUserService.currentUser.id
            }
          />
          <DownloadSewerObjectsButtons
            jobId={currentJobId}
            selectedSewerObjects={jobsSewerObjectsStorage.selectedItems}
            allSewerObjectsCount={jobsSewerObjectsStorage.allItemsCount}
            fileName={currentJobStorage.currentJob.name}
          />
          <RemoveButton
            disabled={!isRemoveEnabled()}
            confirmationAction={removeSelectedSewerObjects}
            confirmationModalTitleKey="JobSewerObjectsPage.remove.confirmation.title"
            confirmationModalTitleContext={{
              sewerObjectsCount: jobsSewerObjectsStorage.selectedItemsCount,
            }}
            confirmationModalSubtitleKey="JobSewerObjectsPage.remove.confirmation"
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid
        itemTypeKey="general.sewer-objects"
        dataGridStorage={jobsSewerObjectsStorage}
        withTopSpacing
      />
    </PageLayout>
  );
});
